import { useEffect, useState, type ReactNode } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";

import { useCapabilities } from "../session/capabilities";
import type { Mode, Period } from "../router/search";

const ALL_PERIOD_OPTIONS: { key: string; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "1y", label: "1Y" },
  { key: "mtd", label: "MTD" },
  { key: "qtd", label: "QTD" },
  { key: "fy", label: "FY" },
  { key: "custom", label: "Custom" },
];

// 7D and 30D are the two windows an O&M reader asks for most, and they were absent
// from the shared control unless a screen passed its own `timeOptions` — only two
// did, so on every other screen they were reachable solely by hand-editing the URL
// (QA, 25 Oct).
const DEFAULT_PERIODS = ["today", "7d", "30d", "1y", "mtd", "qtd", "fy", "custom"];

const MODE_OPTIONS: { key: Mode; label: string; entitlement?: string }[] = [
  { key: "live", label: "Live" },
  { key: "replay", label: "Replay", entitlement: "replay_mode" },
  { key: "forecast", label: "Forecast", entitlement: "generation_forecasting" },
];

function useTimeState(): { period?: Period; mode?: Mode; from?: string; to?: string } {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const matches = useRouterState({ select: (s) => s.matches });
    const leaf = matches[matches.length - 1]?.search as Record<string, unknown> | undefined;
    return {
      period: leaf?.period as Period | undefined,
      mode: (leaf?.mode as Mode | undefined) ?? "live",
      from: typeof leaf?.from === "string" ? (leaf.from as string) : undefined,
      to: typeof leaf?.to === "string" ? (leaf.to as string) : undefined,
    };
  } catch {
    return {};
  }
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/** Which controls to render:
 *  - "period" (default): period chips (+ custom range) and, if entitled, mode chips
 *  - "periods": the period chips ALONE. For a screen that already states its own
 *    live state — Site: Now carries a freshness chip reading "Live · 5 m" — where a
 *    second Live/Replay/Forecast toggle beside it says the same thing twice and
 *    takes the place a reader looks for Today / 7D / 30D.
 *  - "mode": only the Live/Replay/Forecast toggle — all three shown, non-entitled disabled
 */
export type TimeControlVariant = "period" | "periods" | "mode";

/** The two ends of a custom window, held as a draft until applied. */
export function CustomRange(
  { from, to, onApply }:
  { from?: string; to?: string; onApply: (s: Record<string, string>) => void },
): ReactNode {
  const [draftFrom, setDraftFrom] = useState(from ?? "");
  const [draftTo, setDraftTo] = useState(to ?? "");

  // Re-seed when the window changes from OUTSIDE — the day stepper, a period chip,
  // the back button. Without this the inputs would keep showing a stale draft and
  // "Apply" would offer to undo a navigation the reader just made.
  useEffect(() => { setDraftFrom(from ?? ""); }, [from]);
  useEffect(() => { setDraftTo(to ?? ""); }, [to]);

  const complete = Boolean(draftFrom && draftTo && draftFrom <= draftTo);
  const changed = draftFrom !== (from ?? "") || draftTo !== (to ?? "");
  const apply = (): void => {
    if (complete && changed) onApply({ period: "custom", from: draftFrom, to: draftTo });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <input type="date" value={draftFrom} max={draftTo || undefined} style={dateStyle}
        onChange={(e) => setDraftFrom(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
        aria-label="From date" />
      <span style={{ color: "var(--sk-color-ink-3)", fontSize: 11 }}>–</span>
      <input type="date" value={draftTo} min={draftFrom || undefined} style={dateStyle}
        onChange={(e) => setDraftTo(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
        aria-label="To date" />
      <button
        type="button"
        onClick={apply}
        disabled={!complete || !changed}
        // Disabled rather than hidden: a control that appears only once the range is
        // valid moves the row and leaves the reader unsure anything will happen.
        title={!complete
          ? "Choose both ends of the range"
          : !changed ? "This range is already shown" : "Load this range"}
        style={complete && changed ? applyStyle : applyDisabledStyle}
      >
        Apply
      </button>
    </div>
  );
}

export function TimeControl(
  { visiblePeriods, variant = "period" }: { visiblePeriods?: string[]; variant?: TimeControlVariant } = {},
) {
  const { period, mode, from, to } = useTimeState();
  if (!period) return null;
  return (
    <TimeControlInner
      currentPeriod={period}
      currentMode={mode ?? "live"}
      from={from}
      to={to}
      visiblePeriods={visiblePeriods}
      variant={variant}
    />
  );
}

function TimeControlInner({
  currentPeriod,
  currentMode,
  from,
  to,
  visiblePeriods,
  variant,
}: {
  currentPeriod: Period;
  currentMode: Mode;
  from?: string;
  to?: string;
  visiblePeriods?: string[];
  variant: TimeControlVariant;
}) {
  const router = useRouter();
  const caps = useCapabilities();
  // PUSH, not replace. Choosing a period or a mode is a discrete decision a reader
  // expects Back to undo; with `replace: true` the step was overwritten, so Back
  // left the screen entirely and every window they had tried was gone (QA, 25 Oct).
  // `replace` stays only where a step is not a decision — a per-keystroke text
  // filter, or a redirect that should not be a history entry at all.
  const setSearch = (patch: Record<string, unknown>) =>
    void router.navigate({
      search: ((prev: Record<string, unknown>) => ({ ...prev, ...patch })) as never,
    });

  const isEntitled = (m: (typeof MODE_OPTIONS)[number]) =>
    !m.entitlement || caps.entitlements[m.entitlement] === true;

  // "mode" variant: the live-view toggle — all three, non-entitled disabled.
  if (variant === "mode") {
    return (
      <div role="group" aria-label="View mode" style={groupStyle}>
        {MODE_OPTIONS.map((m) => {
          const active = m.key === currentMode;
          const entitled = isEntitled(m);
          if (m.key === "live" && active) {
            return (
              <button key={m.key} type="button" aria-pressed style={liveActiveStyle}>
                <span style={liveDot} aria-hidden />
                {m.label}
              </button>
            );
          }
          return (
            <button
              key={m.key}
              type="button"
              aria-pressed={active}
              disabled={!entitled}
              title={entitled ? undefined : "Not enabled for this tenant"}
              onClick={() => entitled && setSearch({ mode: m.key })}
              style={{ ...(active ? activeStyle : chipStyle), ...(entitled ? {} : disabledChipStyle) }}
            >
              {m.label}
            </button>
          );
        })}
      </div>
    );
  }

  const allowed = visiblePeriods ?? DEFAULT_PERIODS;
  const options = ALL_PERIOD_OPTIONS.filter((p) => allowed.includes(p.key));
  const visibleModes = variant === "periods" ? [] : MODE_OPTIONS.filter(isEntitled);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {/* Period chips */}
      <div role="group" aria-label="Time period" style={groupStyle}>
        {options.map((p) => {
          const active = p.key === currentPeriod;
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={active}
              onClick={() =>
                setSearch(
                  p.key === "custom"
                    ? { period: "custom", from: from ?? isoDaysAgo(29), to: to ?? isoDaysAgo(0) }
                    // A NAMED period carries its own window, so the explicit one
                    // must go with it. `setSearch` merges into the current search,
                    // so these survived otherwise: stepping to 21 Oct and then
                    // pressing Today left `?period=today&from=2026-10-21&to=
                    // 2026-10-21`. The figures were right, because the server
                    // resolves `today` and ignores the pair — but the URL is the
                    // thing a reader copies, bookmarks and sends to someone else,
                    // and that one described a day it would not show. Any screen
                    // honouring from/to more eagerly would have shown the 21st.
                    : { period: p.key, from: undefined, to: undefined },
                )
              }
              style={active ? activeStyle : chipStyle}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Custom date range — edited as a DRAFT, applied on purpose.
          Each input used to write straight to the URL, so picking the start of a
          range refetched the whole dashboard against a half-chosen window: a reader
          changing 25 Sep to 1 Oct saw a load for "1 Oct to 25 Oct" they never asked
          for, before they had touched the second field. A range is one decision and
          is committed once. */}
      {currentPeriod === "custom" ? (
        <CustomRange from={from} to={to} onApply={setSearch} />
      ) : null}

      {/* Mode chips — hidden when only Live is available */}
      {visibleModes.length > 1 ? (
        <div role="group" aria-label="Time mode" style={groupStyle}>
          {visibleModes.map((m) => {
            const active = m.key === currentMode;
            if (m.key === "live" && active) {
              return (
                <button key={m.key} type="button" aria-pressed style={liveActiveStyle}>
                  <span style={liveDot} aria-hidden />
                  {m.label}
                </button>
              );
            }
            return (
              <button
                key={m.key}
                type="button"
                aria-pressed={active}
                onClick={() => setSearch({ mode: m.key })}
                style={active ? activeStyle : chipStyle}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

const applyStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  fontWeight: 600,
  padding: "3px 9px",
  marginLeft: 2,
  color: "var(--sk-color-ink-1)",
  background: "var(--sk-color-surface-1)",
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: 6,
  cursor: "pointer",
};

const applyDisabledStyle: React.CSSProperties = {
  ...applyStyle,
  color: "var(--sk-color-ink-3)",
  background: "transparent",
  cursor: "not-allowed",
};

const dateStyle: React.CSSProperties = {
  height: 24,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control, 6px)",
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
  fontSize: 11,
  padding: "0 6px",
};

const groupStyle: React.CSSProperties = {
  display: "flex",
  gap: 2,
  background: "var(--sk-color-surface-2)",
  borderRadius: "var(--sk-radius-control, 6px)",
  padding: 2,
  flexShrink: 0,
};

const chipStyle: React.CSSProperties = {
  padding: "3px 8px",
  borderRadius: "var(--sk-radius-control, 6px)",
  border: 0,
  background: "transparent",
  color: "var(--sk-color-ink-3)",
  fontSize: 11,
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
  lineHeight: 1.3,
};

const activeStyle: React.CSSProperties = {
  ...chipStyle,
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
  fontWeight: 600,
  boxShadow: "0 1px 2px rgba(10,10,15,.08)",
};

const disabledChipStyle: React.CSSProperties = {
  opacity: 0.4,
  cursor: "default",
};

const liveActiveStyle: React.CSSProperties = {
  ...chipStyle,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-accent)",
  fontWeight: 600,
  boxShadow: "0 1px 2px rgba(10,10,15,.08)",
};

const liveDot: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "var(--sk-color-accent)",
  flexShrink: 0,
};
